import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "STORES", "PROCUREMENT"];
const PLANT_TYPES = ["CRUSHER", "BATCHING", "HOT_MIX"];

export default function PlantsAndBOM() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [tab, setTab] = useState("plants");
  const [plants, setPlants] = useState([]);
  const [boms, setBoms] = useState([]);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: b }, { data: it }, { data: pr }] = await Promise.all([
        api.get("/production-plants"), api.get("/boms"), api.get("/inventory/items"), api.get("/projects"),
      ]);
      setPlants(p); setBoms(b); setItems(it); setProjects(pr);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "—";
  const itemLabel = (id) => { const it = items.find((i) => i.id === id); return it ? `${it.code} — ${it.name}` : "—"; };

  return (
    <div className="space-y-6" data-testid="plants-and-bom-page">
      <PageHeader
        title="Plants & Bill of Materials"
        description="Production plants (crusher, batching, hot-mix) and the recipes (BOMs) that drive production run consumption."
        action={
          <div className="flex gap-1.5">
            <Button size="sm" variant={tab === "plants" ? "default" : "outline"} onClick={() => setTab("plants")} data-testid="tab-plants-button">Plants</Button>
            <Button size="sm" variant={tab === "bom" ? "default" : "outline"} onClick={() => setTab("bom")} data-testid="tab-bom-button">Bill of Materials</Button>
            {canWrite && tab === "plants" && <CreatePlantDialog companies={companies} projects={projects} onCreated={load} />}
            {canWrite && tab === "bom" && <CreateBOMDialog items={items} onCreated={load} />}
          </div>
        }
      />

      {tab === "plants" ? (
        <div className="rounded-xl border border-border bg-card">
          {!loading && plants.length === 0 ? (
            <EmptyState title="No production plants yet" testId="plants-empty-state" />
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Company</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {plants.map((p) => (
                  <TableRow key={p.id} data-testid={`plant-row-${p.code}`}>
                    <TableCell className="font-mono text-xs text-foreground">{p.code}</TableCell>
                    <TableCell className="text-foreground">{p.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.plant_type}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{companyName(p.company_id)}</TableCell>
                    <TableCell><Badge variant={p.is_active ? "default" : "secondary"}>{p.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card">
          {!loading && boms.length === 0 ? (
            <EmptyState title="No bills of material yet" testId="bom-empty-state" />
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Finished item</TableHead><TableHead className="text-right">Version</TableHead><TableHead className="text-right">Components</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {boms.map((b) => (
                  <TableRow key={b.id} data-testid={`bom-row-${b.id}`}>
                    <TableCell className="text-foreground">{itemLabel(b.finished_item_id)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-muted-foreground">v{b.version}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-muted-foreground">{b.components.length}</TableCell>
                    <TableCell><Badge variant={b.is_active ? "default" : "secondary"}>{b.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}

function CreatePlantDialog({ companies, projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", company_id: "", project_id: "", plant_type: "CRUSHER" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/production-plants", { ...form, project_id: form.project_id || null });
      toast.success(`Plant ${form.code} created`);
      setForm({ code: "", name: "", company_id: "", project_id: "", plant_type: "CRUSHER" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="create-plant-button"><Plus className="h-3.5 w-3.5" /> New Plant</Button></DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>New Production Plant</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-plant-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input required value={form.code} onChange={set("code")} className="mt-1.5" data-testid="plant-code-input" /></div>
            <div><Label>Name</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="plant-name-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="plant-company-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plant type</Label>
              <Select value={form.plant_type} onValueChange={(v) => setForm({ ...form, plant_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="plant-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PLANT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Project (optional)</Label>
            <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="plant-project-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not linked</SelectItem>
                {(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-plant-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateBOMDialog({ items, onCreated }) {
  const [open, setOpen] = useState(false);
  const [finishedItemId, setFinishedItemId] = useState("");
  const [components, setComponents] = useState([{ item_id: "", qty_per_unit: "", wastage_pct: "0" }]);
  const [saving, setSaving] = useState(false);

  const addRow = () => setComponents([...components, { item_id: "", qty_per_unit: "", wastage_pct: "0" }]);
  const removeRow = (idx) => setComponents(components.filter((_, i) => i !== idx));
  const setRow = (idx, field, value) => setComponents(components.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/boms", { finished_item_id: finishedItemId, components });
      toast.success("Bill of material created");
      setFinishedItemId("");
      setComponents([{ item_id: "", qty_per_unit: "", wastage_pct: "0" }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="create-bom-button"><Plus className="h-3.5 w-3.5" /> New BOM</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader><DialogTitle>New Bill of Material</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-bom-form">
          <div>
            <Label>Finished item</Label>
            <Select required value={finishedItemId} onValueChange={setFinishedItemId}>
              <SelectTrigger className="mt-1.5" data-testid="bom-finished-item-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Components</Label>
            {components.map((row, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_100px_90px_auto] items-end gap-2" data-testid={`bom-component-row-${idx}`}>
                <Select required value={row.item_id} onValueChange={(v) => setRow(idx, "item_id", v)}>
                  <SelectTrigger data-testid={`bom-component-item-select-${idx}`}><SelectValue placeholder="Item" /></SelectTrigger>
                  <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input required type="number" step="0.0001" placeholder="Qty/unit" value={row.qty_per_unit} onChange={(e) => setRow(idx, "qty_per_unit", e.target.value)} data-testid={`bom-component-qty-input-${idx}`} />
                <Input type="number" step="0.5" placeholder="Waste %" value={row.wastage_pct} onChange={(e) => setRow(idx, "wastage_pct", e.target.value)} data-testid={`bom-component-wastage-input-${idx}`} />
                <Button type="button" size="sm" variant="ghost" disabled={components.length === 1} onClick={() => removeRow(idx)} data-testid={`remove-bom-component-button-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                </Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="add-bom-component-button"><Plus className="h-3.5 w-3.5" /> Add component</Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-bom-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
